/**
 * 무엇을 낼지 고르는 쪽. 부르는 쪽은 일이 날 때마다 `cue`를 부르고, 여기서 걸러 뒤쪽(`AudioBackend`)에 넘긴다.
 * Web Audio를 모르는 순수한 판단이라 시험에서는 가짜 뒤쪽과 가짜 시계로 돌린다(`tests/audio.test.ts`).
 *
 * 거르는 까닭:
 * - **같은 소리의 틈**: 한 틱에 동료 기술이 넷 걸려도 한 번만 낸다. 프레임 수가 아니라 시간으로 센다 — 프레임으로 세면
 *   120Hz 화면과 30Hz 화면의 소리 밀도가 달라진다(계획 리뷰).
 * - **동시 발음 수**: 겹친 소리는 서로 더해져 커진다. 자리가 차면 순위가 낮은 것을 버린다 — 줄을 세워 뒤늦게 내지 않는다.
 *   딱 자른 상한은 아니다: 판의 끝과 장수·적장이 쓰러진 것(`KEEP` 이상)은 자리가 차도 낸다. 울리던 것을 끊고 바꿔
 *   끼우지도 않는다.
 * - **배속**: 다시 보기의 4배·8배는 한 프레임에 수십 틱이 지나간다. 전장 소리를 그대로 내면 한꺼번에 쏟아진다.
 * - **판이 바뀜**: 판을 닫거나 되감으면 예약된 소리를 끊고(`beginSession`), 틈의 기억도 버린다.
 */
import type { GrainReq } from './battle-mix.ts';
import { CUES, KEEP, type CueId } from './cues.ts';

/** 실제로 소리를 내는 쪽. 소리가 꺼졌거나 못 내는 환경에서는 아무것도 하지 않는 것이 맞는 동작이다. */
export interface AudioBackend {
  /** 걸었으면 true. 못 걸었으면(컨텍스트가 섰다 등) 그 소리는 없던 일이다 — 자리와 틈을 차지하지 않는다. */
  play(cue: CueId): boolean;
  /** 전장 소리를 끊는다(판이 바뀜). 단추 소리는 짧아 그대로 둔다. */
  stopBattle(): void;
  /** 전장의 낱소리 하나(`battle-mix.ts`가 묶어 낸 것). */
  grain(req: GrainReq): void;
  /** 전장 깔개의 크기(0이면 잦아든다). 틱마다 부른다 — 부르지 않으면 뒤쪽이 알아서 줄인다(판이 선 것이다). */
  bed(level: number): void;
}

/** 전장 소리가 동시에 날 수 있는 수. 시작값이다 — 타격 소리를 붙일 때 들어 보고 다시 정한다. */
export const BATTLE_VOICES = 8;
export const UI_VOICES = 2;
/** 이 배속부터 전장 소리를 내지 않는다(단추 소리는 낸다). */
export const SILENT_SPEED = 4;

interface Voice {
  end: number;
  pri: number;
}

export class AudioDirector {
  /** 다시 보기의 배속. 싸우는 판은 1. */
  speed = 1;
  /** 소리가 꺼졌다(설정·탭이 가려짐). 꺼진 동안의 일은 버린다 — 켜질 때 몰아서 내지 않는다. */
  muted = false;
  private readonly last = new Map<CueId, number>();
  private readonly voices: Record<'ui' | 'battle', Voice[]> = { ui: [], battle: [] };
  private readonly backend: AudioBackend;
  private readonly now: () => number;

  constructor(backend: AudioBackend, now: () => number) {
    this.backend = backend;
    this.now = now;
  }

  /** 소리를 청한다. 냈으면 true. */
  cue(id: CueId): boolean {
    if (this.muted) return false;
    const def = CUES[id];
    if (def.bus === 'battle' && this.speed >= SILENT_SPEED) return false;
    const t = this.now();
    // 2배에서는 같은 소리의 틈도 두 배 — 일은 두 배로 잦게 나는데 소리의 길이는 그대로다.
    const gap = def.bus === 'battle' ? def.gapMs * Math.max(1, this.speed) : def.gapMs;
    const prev = this.last.get(id);
    if (prev !== undefined && t - prev < gap) return false;
    const live = this.voices[def.bus];
    for (let i = live.length - 1; i >= 0; i--) if (live[i].end <= t) live.splice(i, 1);
    const cap = def.bus === 'ui' ? UI_VOICES : BATTLE_VOICES;
    if (live.length >= cap && def.pri < KEEP) return false;
    if (!this.backend.play(id)) return false;
    this.last.set(id, t);
    live.push({ end: t + def.durMs, pri: def.pri });
    return true;
  }

  /**
   * 한 틱의 낱소리와 깔개. 신호(`cue`)와 달리 틈·자리를 세지 않는다 — 묶기와 상한은 `BattleMix`가 이미 했다.
   * 꺼졌거나 다시 보기의 4배부터는 내지 않고 깔개도 내린다.
   */
  grains(reqs: readonly GrainReq[], bedLevel: number): void {
    if (this.muted || this.speed >= SILENT_SPEED) {
      this.backend.bed(0);
      return;
    }
    for (const r of reqs) this.backend.grain(r);
    this.backend.bed(bedLevel);
  }

  /** 낱소리만(말발굽). 깔개는 건드리지 않는다 — 깔개는 타격의 잦기를 따른다. */
  sprinkle(reqs: readonly GrainReq[]): void {
    if (this.muted || this.speed >= SILENT_SPEED) return;
    for (const r of reqs) this.backend.grain(r);
  }

  /** 깔개를 내린다(판이 끝났다). */
  bedOff(): void {
    this.backend.bed(0);
  }

  /** 판을 새로 열거나 닫거나 되감았다: 나던 전장 소리를 끊고 기억을 비운다. */
  beginSession(speed = 1): void {
    this.speed = speed;
    this.voices.battle.length = 0;
    for (const id of [...this.last.keys()]) if (CUES[id].bus === 'battle') this.last.delete(id);
    this.backend.stopBattle();
  }
}
